import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../base/button/button";
import { cn } from "../../../utils";
import { useReorderSensors } from "../../../hooks/useReorderSensors";
import { DragHandle } from "../drag-handle";
import { InputFormDialog } from "../dialogs";
import { STEP_FIELD_ICONS } from "./stepFieldIcons";
import { FieldSectionLabel } from "./FieldSectionLabel";
import { StepNumberBadge } from "./StepNumberBadge";
import { reorderInputs } from "./stepInputsReorder";
/**
 * Editable Inputs section shared by the manual-step and assignment side panels.
 * Owns the add/edit dialog state; the host only supplies the current inputs and
 * an `onChange` that persists the next array. Inputs flagged `notRemovable`
 * (assignment-seeded anchor links) render without a remove control and open
 * read-only in the dialog, so the upstream connection can't be severed here.
 */ export function EditableInputsSection({ inputs, currentStepId, mode = "draft", eventTypes, processSteps, canAdd = true, onChange }) {
    const { t } = useTranslation();
    const [showInputModal, setShowInputModal] = useState(false);
    const [editingInputIndex, setEditingInputIndex] = useState(null);
    const editingInput = editingInputIndex !== null ? inputs?.[editingInputIndex] : undefined;
    // Draft-mode previous_output inputs are managed via flow connections, so the
    // dialog only views them — editing happens by connecting/disconnecting steps.
    const editingInputReadOnly = mode === "draft" && editingInput?.inputType === "previous_output";
    const linkableSteps = useMemo(()=>processSteps?.filter((s)=>s._id !== currentStepId), [
        processSteps,
        currentStepId
    ]);
    // Source step id → its flow step number, so a `previous_output` input can
    // show which step it draws from (e.g. the ③ badge mirrors the canvas node).
    const stepNumberById = useMemo(()=>{
        const map = new Map();
        for (const s of processSteps ?? []){
            if (s.stepNumber != null) map.set(s._id, s.stepNumber);
        }
        return map;
    }, [
        processSteps
    ]);
    const handleSaveInput = useCallback((data)=>{
        const current = inputs ?? [];
        const next = editingInputIndex !== null ? current.map((v, i)=>i === editingInputIndex ? data : v) : [
            ...current,
            data
        ];
        onChange(next);
        setShowInputModal(false);
        setEditingInputIndex(null);
    }, [
        editingInputIndex,
        inputs,
        onChange
    ]);
    const handleRemoveInput = useCallback((index)=>{
        const current = inputs ?? [];
        onChange(current.filter((_, i)=>i !== index));
    }, [
        inputs,
        onChange
    ]);
    const sensors = useReorderSensors();
    // Input order is array order (no semantic meaning) — reorder in place and
    // persist the whole array, same as add / edit / remove.
    const handleDragEnd = useCallback((event)=>{
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        onChange(reorderInputs(inputs ?? [], active.id, over.id));
    }, [
        inputs,
        onChange
    ]);
    const openEditAt = useCallback((index)=>{
        setEditingInputIndex(index);
        setShowInputModal(true);
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                icon: STEP_FIELD_ICONS.input,
                hint: t("processFlow.inputsHint"),
                children: t("processFlow.inputs")
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1.5",
                children: /*#__PURE__*/ _jsx(DndContext, {
                    sensors: sensors,
                    collisionDetection: closestCenter,
                    onDragEnd: handleDragEnd,
                    children: /*#__PURE__*/ _jsx(SortableContext, {
                        items: (inputs ?? []).map((_, i)=>String(i)),
                        strategy: verticalListSortingStrategy,
                        children: (inputs ?? []).map((input, index)=>/*#__PURE__*/ _jsx(SortableInputRow, {
                                input: input,
                                index: index,
                                stepNumberById: stepNumberById,
                                onEdit: openEditAt,
                                onRemove: handleRemoveInput
                            }, index))
                    })
                })
            }),
            canAdd && /*#__PURE__*/ _jsxs(Button, {
                variant: "ghost",
                size: "small",
                className: "mt-1.5 w-full h-7 text-muted-foreground hover:text-fg-secondary hover:bg-muted",
                onClick: ()=>{
                    setEditingInputIndex(null);
                    setShowInputModal(true);
                },
                "data-field-issue-focus": "",
                children: [
                    /*#__PURE__*/ _jsx(Plus, {
                        className: "w-3.5 h-3.5 mr-1"
                    }),
                    t("processFlow.editable.addInput")
                ]
            }),
            /*#__PURE__*/ _jsx(InputFormDialog, {
                open: showInputModal,
                onOpenChange: (open)=>{
                    setShowInputModal(open);
                    if (!open) setEditingInputIndex(null);
                },
                onSubmit: handleSaveInput,
                input: editingInput,
                eventTypes: eventTypes ?? [],
                processSteps: linkableSteps,
                readOnly: editingInputReadOnly,
                existingInputs: inputs,
                editingInputIndex: editingInputIndex
            })
        ]
    });
}
/**
 * One draggable input row: a plain container holding three sibling controls —
 * the drag grip, the edit button (the input's name/description), and the remove
 * button. Keeping them siblings (rather than nesting them inside one clickable
 * row) avoids interactive-in-interactive nesting and lets each be its own
 * accessible control. Uses the array index as the sortable id — stable for the
 * duration of a drag (nothing re-renders the list mid-drag) and reset on drop
 * when the reordered array persists.
 */ function SortableInputRow({ input, index, stepNumberById, onEdit, onRemove }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: String(index)
    });
    // Derived event inputs arrive hydrated from their event node
    // (resolve-recording-inputs) — the stored fields ARE current.
    const { name: displayName, description: displayDescription } = input;
    return /*#__PURE__*/ _jsxs("div", {
        ref: setNodeRef,
        style: {
            transform: CSS.Transform.toString(transform),
            transition
        },
        className: cn("flex items-start gap-1 text-sm bg-surface-subtle hover:bg-muted px-2 py-1.5 rounded-sm group transition-colors", isDragging && "relative z-10 opacity-90 shadow-lg"),
        children: [
            /*#__PURE__*/ _jsx(DragHandle, {
                label: t("processFlow.editable.reorderInput"),
                setActivatorNodeRef: setActivatorNodeRef,
                attributes: attributes,
                listeners: listeners,
                size: 12,
                className: "mt-0.5 shrink-0 text-fg-disabled hover:text-muted-foreground"
            }),
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: ()=>onEdit(index),
                className: "flex-1 min-w-0 text-left",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1",
                        children: [
                            (input.sourceSteps ?? []).map((src)=>{
                                const num = stepNumberById.get(src.stepId);
                                return num == null ? null : /*#__PURE__*/ _jsx(StepNumberBadge, {
                                    number: num,
                                    title: t("processFlow.stepPrefix", {
                                        number: num
                                    })
                                }, src.stepId);
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium truncate",
                                children: displayName
                            })
                        ]
                    }),
                    displayDescription && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-fg-secondary mt-0.5",
                        children: displayDescription
                    })
                ]
            }),
            !input.notRemovable && /*#__PURE__*/ _jsx("button", {
                type: "button",
                "aria-label": t("processFlow.editable.removeInput"),
                title: t("processFlow.editable.removeInput"),
                onClick: (e)=>{
                    e.stopPropagation();
                    onRemove(index);
                },
                className: "p-0.5 shrink-0 text-fg-subtle hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity",
                children: /*#__PURE__*/ _jsx(X, {
                    className: "w-3 h-3"
                })
            })
        ]
    });
}

//# sourceMappingURL=EditableInputsSection.js.map